import React, { useState, useEffect } from "react";
import { UTILS } from "utils";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useEditCountryMutation,
  useGetAddedCountriesMutation,
} from "redux/shipping-methods/shipping-service";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppSelect from "components/ui/AppSelect";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import { calculateDeliveryTimeInMins } from "features/shipping_methods/helpers";

const convertMinsToDeliveryTime = (minutesValue) => {
  const parsedMinutes =
    typeof minutesValue === "string"
      ? Number(minutesValue.replace(/[^\d]/g, ""))
      : Number(minutesValue);
  const totalMins = Number.isFinite(parsedMinutes) ? parsedMinutes : 0;
  const days = Math.floor(totalMins / 1440);
  const remainderAfterDays = totalMins % 1440;
  const hours = Math.floor(remainderAfterDays / 60);
  const minutes = remainderAfterDays % 60;
  return { Days: days, Hours: hours, Minutes: minutes };
};

const UpdateCountry = ({ open, onClose, selectedCountries }) => {
  const { selectedStore } = useSelector((state) => state.AreasReducer);
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const { t } = useTranslation();
  const [editCountry] = useEditCountryMutation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [getAddedCountries] = useGetAddedCountriesMutation();
  const [state, setState] = useState({
    minOrder: "",
    deliveryFee: "",
    deliveryTime: { Hours: 0, Minutes: 0, Days: 0 },
  });
  const handleValues = (key, val) => {
    setState({ ...state, [key]: val });
  };
  useEffect(() => {
    if (!open || !selectedCountries?.length) return;
    const first = selectedCountries[0];
    setState({
      minOrder: first?.minOrder != null ? String(first.minOrder) : "",
      deliveryFee: first?.deliveryFee != null ? String(first.deliveryFee) : "",
      deliveryTime: convertMinsToDeliveryTime(first?.deliveryTime),
    });
  }, [open, selectedCountries]);

  const edit_country = UTILS.withTryCatch(async () => {
    const delTime = calculateDeliveryTimeInMins({
      deliveryTime: state.deliveryTime,
    });
    const userDetail = UTILS.getCurrentUser();
    let objectArray = [];
    selectedCountries.forEach((country) => {
      objectArray.push({
        countryID: country.countryID._id,
        deliveryFee: +Number(state.deliveryFee),
        deliveryTime: +Number(delTime),
        minOrder: +Number(state.minOrder),
        _id: country._id,
      });
    });

    const obj = {
      brandId: userDetail.brandId,
      storeId: selectedStore.value,
      language: currentLanguage,
      areas: objectArray,
    };
    const getObj = {
      brandId: userDetail.brandId,
      storeId: selectedStore.value,
      language: currentLanguage,
    };
    await editCountry(obj).unwrap();
    await getAddedCountries(getObj);
    onClose();
  });

  let isValid = () => {
    if (
      isLoading ||
      state.minOrder === "" ||
      state.deliveryFee === "" ||
      selectedCountries.length <= 0 ||
      (state?.deliveryTime?.Days === 0 &&
        state?.deliveryTime?.Hours === 0 &&
        state?.deliveryTime?.Minutes === 0)
    )
      return false;
    return true;
  };
  const handleInputValue = (key, val) => {
    const value = UTILS.NumberConversion(val);
    handleValues(key, value);
  };
  const handleTimeValues = (key, value) =>
    setState((prev) => ({
      ...prev,
      deliveryTime: { ...prev.deliveryTime, [key]: Number(value || 0) },
    }));

  const daysOptions = Array.from({ length: 8 }, (_, value) => ({
    label: `${value} ${t("Days")}`,
    value,
  }));
  const hourOptions = Array.from({ length: 24 }, (_, value) => ({
    label: `${value} ${t("Hours")}`,
    value,
  }));
  const minuteOptions = Array.from({ length: 12 }, (_, index) => ({
    label: `${index * 5} ${t("Minutes")}`,
    value: index * 5,
  }));

  const footer = (
    <div className="flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={isLoading}>
        {t("Discard Changes")}
      </AppOutlineButton>
      <AppButton
        variant="primary"
        onClick={edit_country}
        disabled={!isValid()}
        loading={isLoading}
      >
        {t("Save Changes")}
      </AppButton>
    </div>
  );

  return (
    <AppModal
      title={`${t("Edit")} ${t("Delivery Country")}`}
      open={open}
      onClose={onClose}
      width={650}
      footer={footer}
      loading={isLoading}
    >
      <div className="grid grid-cols-2 gap-4">
        <AppInput
          label={t("Min. Basket Value")}
          value={state.minOrder}
          onInput={(e) => handleInputValue("minOrder", e.target.value)}
        />
        <AppInput
          label={t("Delivery Fee")}
          value={state.deliveryFee}
          onInput={(e) => handleInputValue("deliveryFee", e.target.value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
      <div className="grid grid-cols-3 gap-4">
        <AppSelect
          placeholder={t("Select Days")}
          value={state.deliveryTime.Days}
          options={daysOptions}
          onChange={(value) => handleTimeValues("Days", value)}
        />
        <AppSelect
          placeholder={t("Select Hours")}
          value={state.deliveryTime.Hours}
          options={hourOptions}
          onChange={(value) => handleTimeValues("Hours", value)}
        />
        <AppSelect
          placeholder={t("Select Minutes")}
          value={state.deliveryTime.Minutes}
          options={minuteOptions}
          onChange={(value) => handleTimeValues("Minutes", value)}
        />
      </div>
    </AppModal>
  );
};

export default UpdateCountry;
